import { useCallback, memo, ReactNode, FC } from 'react';

import { useTranslator } from '@picsart/rc/services/localization';
import Head from 'next/head';

import { useLoadMorePosts, LoadMoreReturnType } from '~/hooks/useLoadMorePosts';
import TopBar, { ITopBarPropsModel } from '~/components/TopBar/TopBar';
import { IPostListItemModel } from '~/models/postModels';
import useDecodeString from '~/hooks/useDecodeString';
import useBaseTemplateStyles from './BaseTemplateJSS';
import SvgIcon from '~/components/SvgIcon/SvgIcon';
import { PageClickModel } from '~/services/types';
import Layout from '~/components/Layout/Layout';

export type TemplateProps = {
  loadMore: (
    page: number,
    query: string,
    language: string,
  ) => LoadMoreReturnType;
  initialPosts?: IPostListItemModel[];
  topBarProps?: ITopBarPropsModel;
  analyticsEvent?: PageClickModel;
  initialPagesCount: number;
  description?: string;
  title?: string;
  query?: string;
  meta?: string;
};

type BaseTemplateProps = TemplateProps & {
  renderContent?: (posts: IPostListItemModel[]) => ReactNode;
};

const BaseTemplate: FC<BaseTemplateProps> = ({
  initialPagesCount,
  renderContent,
  initialPosts,
  topBarProps,
  description,
  loadMore,
  title,
  meta,
}) => {
  const { buttonWrapper, buttonInner, button, buttonIcon } =
    useBaseTemplateStyles();
  const translate = useTranslator();

  const [posts, canLoadMore, handleLoadMoreClick] = useLoadMorePosts(
    initialPosts,
    initialPagesCount,
    loadMore,
  );
  const renderTop = useCallback<() => ReactNode>(
    () => (topBarProps ? <TopBar {...topBarProps} /> : null),
    [topBarProps],
  );
  const decodedMeta = useDecodeString(meta);
  return (
    <>
      {meta && <Head>{decodedMeta}</Head>}
      <Layout top={renderTop()} title={title} description={description}>
        {renderContent(posts)}
        {canLoadMore && (
          <div className={buttonWrapper}>
            <button
              onClick={handleLoadMoreClick}
              className={button}
              type="button"
            >
              <span className={buttonInner}>
                {translate('Load more')}
                <SvgIcon name="refresh-gen" className={buttonIcon} />
              </span>
            </button>
          </div>
        )}
      </Layout>
    </>
  );
};

const defaultProps = {
  renderContent: () => null,
  initialPagesCount: 0,
  loadMore: () => null,
  topBarProps: null,
  initialPosts: [],
  description: '',
  title: '',
  query: '',
  meta: '',
};
BaseTemplate.defaultProps = defaultProps as BaseTemplateProps;

export default memo<BaseTemplateProps>(BaseTemplate);
